import React, { useEffect, Suspense, lazy } from 'react';
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { AuthGuard } from "@/features/shared/AuthGuard";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { initializeAuthInterceptor } from "./lib/authInterceptor";
import { GlobalAdminGuard } from "@/components/GlobalAdminGuard";

// Keep critical components non-lazy for faster initial load
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";
import { Auth } from "./pages/Auth";

// Lazy load public marketing pages
const FeaturesPage = lazy(() => import("./pages/public/FeaturesPage"));
// Pricing page hidden for now
// const PricingPage = lazy(() => import("./pages/public/PricingPage"));
const AboutPage = lazy(() => import("./pages/public/AboutPage"));
const HowItWorksPage = lazy(() => import("./pages/public/HowItWorksPage"));
const ContactPage = lazy(() => import("./pages/public/ContactPage"));
const BlogPage = lazy(() => import("./pages/public/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/public/BlogPostPage"));
const PrivacyPage = lazy(() => import("./pages/public/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/public/TermsPage"));
const FoodTruckAccountingPage = lazy(() => import("./pages/public/FoodTruckAccountingPage"));
const FoodHallAccountingPage = lazy(() => import("./pages/public/FoodHallAccountingPage"));
const RestaurantAccountingPage = lazy(() => import("./pages/public/RestaurantAccountingPage"));
import { AuthCallback } from "./pages/AuthCallback";
import { ResetPassword } from "./pages/ResetPassword";
import Index from "./pages/Index";
import { DashboardRouter } from "@/features/dashboard/DashboardRouter";

// Lazy load onboarding routes
const OnboardingHub = lazy(() => import("./pages/OnboardingHub").then(m => ({ default: m.OnboardingHub })));
const OnboardingBusinessProfile = lazy(() => import("./pages/OnboardingBusinessProfile").then(m => ({ default: m.OnboardingBusinessProfile })));
const OnboardingPayroll = lazy(() => import("./pages/OnboardingPayroll").then(m => ({ default: m.OnboardingPayroll })));
const OnboardingBankAccountTraining = lazy(() => import("./pages/OnboardingBankAccountTraining").then(m => ({ default: m.OnboardingBankAccountTraining })));
const OnboardingCOGSVendor = lazy(() => import("./pages/OnboardingCOGSVendor").then(m => ({ default: m.OnboardingCOGSVendor })));
const OnboardingPOSTraining = lazy(() => import("./pages/OnboardingPOSTraining").then(m => ({ default: m.OnboardingPOSTraining })));
const OnboardingOperations = lazy(() => import("./pages/OnboardingOperations").then(m => ({ default: m.OnboardingOperations })));
const OnboardingInventorySetup = lazy(() => import("./pages/OnboardingInventorySetup").then(m => ({ default: m.OnboardingInventorySetup })));
const OnboardingPrepMenuRecipes = lazy(() => import("./pages/OnboardingPrepMenuRecipes").then(m => ({ default: m.OnboardingPrepMenuRecipes })));
const OnboardingMenuPricing = lazy(() => import("./pages/OnboardingMenuPricing").then(m => ({ default: m.OnboardingMenuPricing })));
// ARCHIVED: Addons section for MVP (save issue to fix later; tax center archived)
// const OnboardingAccountingAddons = lazy(() => import("./pages/OnboardingAccountingAddons").then(m => ({ default: m.OnboardingAccountingAddons })));

// Lazy load dashboard routes
const Dashboard = lazy(() => import("@/features/dashboard/Dashboard").then(m => ({ default: m.Dashboard })));
const GlobalCompaniesManagement = lazy(() => import("@/components/GlobalCompaniesManagement").then(m => ({ default: m.GlobalCompaniesManagement })));
const GlobalUsersManagement = lazy(() => import("@/components/GlobalUsersManagement").then(m => ({ default: m.GlobalUsersManagement })));
const GlobalPackagesManagement = lazy(() => import("@/components/GlobalPackagesManagement").then(m => ({ default: m.GlobalPackagesManagement })));
const GlobalSyncLogs = lazy(() => import("@/components/GlobalSyncLogs").then(m => ({ default: m.GlobalSyncLogs })));
const PantryManagement = lazy(() => import("@/components/PantryManagement").then(m => ({ default: m.PantryManagement })));
const MenuManagement = lazy(() => import("@/components/MenuManagement").then(m => ({ default: m.MenuManagement })));
const ExpenseManagement = lazy(() => import("@/components/ExpenseManagement").then(m => ({ default: m.ExpenseManagement })));
const SalesReconciliation = lazy(() => import("@/components/SalesReconciliation").then(m => ({ default: m.SalesReconciliation })));
const DataCenterPage = lazy(() => import("@/features/data-center/DataCenterPage").then(m => ({ default: m.DataCenterPage })));
const AITrainingPage = lazy(() => import("@/pages/AITrainingPage").then(m => ({ default: m.AITrainingPage })));
const PLPage = lazy(() => import("@/components/placeholders/PLPage").then(m => ({ default: m.PLPage })));
const CashFlowPage = lazy(() => import("@/components/placeholders/CashFlowPage").then(m => ({ default: m.CashFlowPage })));
const BalanceSheetPage = lazy(() => import("@/components/placeholders/BalanceSheetPage").then(m => ({ default: m.BalanceSheetPage })));
const TaxCenterPage = lazy(() => import("@/components/placeholders/TaxCenterPage").then(m => ({ default: m.TaxCenterPage })));
const SalesPage = lazy(() => import("@/features/accounting/SalesPage").then(m => ({ default: m.SalesPage })));
const VendorsPage = lazy(() => import("@/components/placeholders/VendorsPage").then(m => ({ default: m.VendorsPage })));
const SettingsPage = lazy(() => import("@/components/placeholders/SettingsPage").then(m => ({ default: m.SettingsPage })));
const EmployeesPage = lazy(() => import("@/components/placeholders/EmployeesPage").then(m => ({ default: m.EmployeesPage })));
const PayrollInbox = lazy(() => import("@/pages/PayrollInbox").then(m => ({ default: m.default })));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage").then(m => ({ default: m.default })));
const HelpCenterPage = lazy(() => import("@/components/HelpCenterPage").then(m => ({ default: m.HelpCenterPage })));
// ARCHIVED: My Company - merged into Settings
// const MyCompanyPage = lazy(() => import("@/components/MyCompanyPage").then(m => ({ default: m.MyCompanyPage })));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const DollarBillChatPage = lazy(() => import("@/components/DollarBillChatPage").then(m => ({ default: m.DollarBillChatPage })));

// Loading component for Suspense boundaries
const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </div>
);

// Optimized QueryClient configuration to reduce database egress
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default to reduce redundant queries
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      // Retry failed requests only once
      retry: 1,
      // Don't refetch on window focus to reduce unnecessary requests
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect unless data is stale
      refetchOnReconnect: 'always',
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
      // Query deduplication is enabled by default in React Query v5
      // Network mode: prefer cache over network when possible
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations only once
      retry: 1,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

const App = () => {
  // Initialize global auth interceptor on mount
  useEffect(() => {
    const cleanup = initializeAuthInterceptor();
    return cleanup;
  }, []);

  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<Suspense fallback={<RouteLoadingFallback />}><FeaturesPage /></Suspense>} />
          {/* Pricing page hidden for now */}
          <Route path="/about" element={<Suspense fallback={<RouteLoadingFallback />}><AboutPage /></Suspense>} />
          <Route path="/how-it-works" element={<Suspense fallback={<RouteLoadingFallback />}><HowItWorksPage /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<RouteLoadingFallback />}><ContactPage /></Suspense>} />
          <Route path="/blog" element={<Suspense fallback={<RouteLoadingFallback />}><BlogPage /></Suspense>} />
          <Route path="/blog/:slug" element={<Suspense fallback={<RouteLoadingFallback />}><BlogPostPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<RouteLoadingFallback />}><PrivacyPage /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<RouteLoadingFallback />}><TermsPage /></Suspense>} />
          <Route path="/food-truck-accounting" element={<Suspense fallback={<RouteLoadingFallback />}><FoodTruckAccountingPage /></Suspense>} />
          <Route path="/food-hall-accounting" element={<Suspense fallback={<RouteLoadingFallback />}><FoodHallAccountingPage /></Suspense>} />
          <Route path="/restaurant-accounting" element={<Suspense fallback={<RouteLoadingFallback />}><RestaurantAccountingPage /></Suspense>} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingHub />
              </Suspense>
            </AuthGuard>
          } />
          {/* Individual Module Routes */}
          <Route path="/onboarding/business-profile" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingBusinessProfile />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/payroll" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingPayroll />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/bank-account-training" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingBankAccountTraining />
              </Suspense>
            </AuthGuard>
          } />
          {/* Redirect old routes to training modules */}
          <Route path="/onboarding/bank-accounts" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingBankAccountTraining />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/vendors" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingCOGSVendor />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/cogs-vendor" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingCOGSVendor />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/pos-training" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingPOSTraining />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/inventory-setup" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingInventorySetup />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/prep-menu-recipes" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingPrepMenuRecipes />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/menu-pricing" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingMenuPricing />
              </Suspense>
            </AuthGuard>
          } />
          <Route path="/onboarding/operations" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingOperations />
              </Suspense>
            </AuthGuard>
          } />
          {/* ARCHIVED: Addons section for MVP */}
          {/* <Route path="/onboarding/accounting-addons" element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <OnboardingAccountingAddons />
              </Suspense>
            </AuthGuard>
          } /> */}
        <Route path="/dashboard" element={
          <AuthGuard>
            <OnboardingProvider>
              <TooltipProvider>
                <Index />
              </TooltipProvider>
            </OnboardingProvider>
          </AuthGuard>
        }>
            <Route index element={<DashboardRouter />} />
            
            {/* Global Admin Routes */}
            <Route path="admin/companies" element={
              <GlobalAdminGuard>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <div className="container mx-auto px-6 py-8">
                    <GlobalCompaniesManagement />
                  </div>
                </Suspense>
              </GlobalAdminGuard>
            } />
            <Route path="admin/users" element={
              <GlobalAdminGuard>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <div className="container mx-auto px-6 py-8">
                    <GlobalUsersManagement />
                  </div>
                </Suspense>
              </GlobalAdminGuard>
            } />
            <Route path="admin/packages" element={
              <GlobalAdminGuard>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <div className="container mx-auto px-6 py-8">
                    <GlobalPackagesManagement />
                  </div>
                </Suspense>
              </GlobalAdminGuard>
            } />
            <Route path="admin/sync-logs" element={
              <GlobalAdminGuard>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <div className="container mx-auto px-6 py-8">
                    <GlobalSyncLogs />
                  </div>
                </Suspense>
              </GlobalAdminGuard>
            } />
            
            {/* Accounting Routes */}
            <Route path="accounting/pl" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <PLPage />
                </div>
              </Suspense>
            } />
            <Route path="accounting/cash-flow" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <CashFlowPage />
                </div>
              </Suspense>
            } />
            <Route path="accounting/balance-sheet" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <BalanceSheetPage />
                </div>
              </Suspense>
            } />
            <Route path="accounting/tax-center" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <TaxCenterPage />
                </div>
              </Suspense>
            } />
            <Route path="accounting/reconciliation" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <SalesReconciliation />
                </div>
              </Suspense>
            } />
            
            {/* Finance Routes */}
            <Route path="finance/sales" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <SalesPage />
                </div>
              </Suspense>
            } />
            <Route path="finance/expenses" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <ExpenseManagement />
                </div>
              </Suspense>
            } />
            <Route path="finance/payroll-inbox" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <PayrollInbox />
              </Suspense>
            } />
            <Route path="notifications" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <NotificationsPage />
              </Suspense>
            } />

            {/* Operations Routes */}
            <Route path="operations/inventory" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <PantryManagement />
                </div>
              </Suspense>
            } />
            <Route path="operations/menu" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <MenuManagement />
                </div>
              </Suspense>
            } />
            
            {/* Vendors Route */}
            <Route path="vendors" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <VendorsPage />
                </div>
              </Suspense>
            } />
            
            {/* Employee Routes */}
            <Route path="employees/w2" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <EmployeesPage />
                </div>
              </Suspense>
            } />
            <Route path="employees/contractors" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <EmployeesPage />
                </div>
              </Suspense>
            } />
            
            {/* Data Center — single page: uploads + integrations */}
            <Route path="data-center" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <DataCenterPage />
                </div>
              </Suspense>
            } />
            <Route path="data-center/sales-deposits" element={<Navigate to="/dashboard/data-center#sales-deposits" replace />} />
            <Route path="data-center/sales-quantities" element={<Navigate to="/dashboard/data-center#sales-quantities" replace />} />
            <Route path="data-center/receipts" element={<Navigate to="/dashboard/data-center#receipts" replace />} />
            <Route path="data-center/ai-training" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <AITrainingPage />
                </div>
              </Suspense>
            } />

            {/* Integrations */}
            <Route path="integrations" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <IntegrationsPage />
                </div>
              </Suspense>
            } />
            
            {/* ARCHIVED: My Company route - merged into Settings */}
            {/* <Route path="company" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <MyCompanyPage />
                </div>
              </Suspense>
            } /> */}
            <Route path="settings" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-6 py-8">
                  <SettingsPage />
                </div>
              </Suspense>
            } />
            <Route path="help" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <HelpCenterPage />
              </Suspense>
            } />
            <Route path="assistant/dollar-bill" element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <div className="container mx-auto px-0 py-0">
                  <DollarBillChatPage />
                </div>
              </Suspense>
            } />
          </Route>
          
          {/* Shortcut route to Dollar Bill assistant */}
          <Route
            path="/assistant/dollar-bill"
            element={<Navigate to="/dashboard/assistant/dollar-bill" replace />}
          />

          {/* Catch all 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
